import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Shield, Save, RotateCcw, ChevronDown, Lock } from "lucide-react";

export type ViolationAction = "warn" | "pause" | "kick" | "revoke_enrollment";
export type WatermarkIdentity = "email" | "phone" | "both";

export type SecuritySettings = {
  block_devtools: boolean;
  block_right_click: boolean;
  block_copy: boolean;
  block_screenshot: boolean;
  tab_switch_pause: boolean;
  tab_switch_flag: boolean;
  tab_switch_threshold: number;
  tab_switch_window_seconds: number;
  watermark_enabled: boolean;
  watermark_flash_enabled: boolean;
  watermark_flash_interval_seconds: number;
  /** Interval (sec) for the small top-left corner mark. Set 0 to disable corner mark. */
  watermark_corner_tl_interval_seconds: number;
  /** Interval (sec) for the small bottom-right corner mark. Set 0 to disable corner mark. */
  watermark_corner_br_interval_seconds: number;
  legal_warning_enabled: boolean;
  // Advanced
  max_devices_per_user: number;
  block_vm_emulator: boolean;
  block_multi_monitor: boolean;
  violation_action: ViolationAction;
  watermark_identity: WatermarkIdentity;
};

export const DEFAULT_SECURITY: SecuritySettings = {
  block_devtools: true,
  block_right_click: true,
  block_copy: true,
  block_screenshot: true,
  tab_switch_pause: true,
  tab_switch_flag: true,
  tab_switch_threshold: 3,
  tab_switch_window_seconds: 30,
  watermark_enabled: true,
  watermark_flash_enabled: true,
  watermark_flash_interval_seconds: 25,
  watermark_corner_tl_interval_seconds: 17,
  watermark_corner_br_interval_seconds: 23,
  legal_warning_enabled: true,
  max_devices_per_user: 2,
  block_vm_emulator: true,
  block_multi_monitor: true,
  violation_action: "revoke_enrollment",
  watermark_identity: "both",
};

interface Props {
  sessionToken: string;
  targetType: "course" | "premium_group";
  targetId: string;
  initialSettings?: Partial<SecuritySettings> | null;
  onSaved?: (settings: SecuritySettings) => void;
}

const SecuritySettingsPanel = ({ sessionToken, targetType, targetId, initialSettings, onSaved }: Props) => {
  const [settings, setSettings] = useState<SecuritySettings>({ ...DEFAULT_SECURITY, ...(initialSettings || {}) });
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setSettings({ ...DEFAULT_SECURITY, ...(initialSettings || {}) });
  }, [initialSettings, targetId]);

  const update = <K extends keyof SecuritySettings>(key: K, value: SecuritySettings[K]) => {
    setSettings((s) => ({ ...s, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("manage-certificates", {
      body: { action: "update_security_settings", sessionToken, targetType, targetId, settings },
    });
    setSaving(false);
    if (error || !data?.success) {
      toast({ title: "Save failed", description: error?.message || "Could not save settings", variant: "destructive" });
      return;
    }
    toast({ title: "✅ Security settings saved" });
    onSaved?.(settings);
  };

  const handleReset = () => setSettings(DEFAULT_SECURITY);

  const Toggle = ({ k, label, hint }: { k: keyof SecuritySettings; label: string; hint?: string }) => (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <Label className="text-sm font-medium cursor-pointer" htmlFor={`sec-${k}`}>{label}</Label>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <Switch
        id={`sec-${k}`}
        checked={settings[k] as boolean}
        onCheckedChange={(v) => update(k, v as never)}
      />
    </div>
  );

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-primary" />
        <h4 className="text-sm font-semibold">Security Settings</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Core Protections</p>
          <Toggle k="block_devtools" label="Block DevTools" hint="Detect & pause when developer tools open" />
          <Toggle k="block_right_click" label="Block Right-Click" hint="Disables context menu on the player" />
          <Toggle k="block_copy" label="Block Copy / View Source" hint="Blocks Ctrl+C, Ctrl+U, Ctrl+S shortcuts" />
          <Toggle k="block_screenshot" label="Block Screenshots & Recording" hint="Blocks PrintScreen, MediaRecorder, getDisplayMedia" />
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Tab Switching</p>
          <Toggle k="tab_switch_pause" label="Pause on Tab Switch" hint="Pauses video when student leaves the tab" />
          <Toggle k="tab_switch_flag" label="Flag Rapid Switching" hint="Reports as violation if threshold exceeded" />
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div>
              <Label className="text-xs">Threshold</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={settings.tab_switch_threshold}
                onChange={(e) => update("tab_switch_threshold", Math.max(1, parseInt(e.target.value) || 3))}
                className="h-8 text-xs"
                disabled={!settings.tab_switch_flag}
              />
            </div>
            <div>
              <Label className="text-xs">Window (sec)</Label>
              <Input
                type="number"
                min={5}
                max={300}
                value={settings.tab_switch_window_seconds}
                onChange={(e) => update("tab_switch_window_seconds", Math.max(5, parseInt(e.target.value) || 30))}
                className="h-8 text-xs"
                disabled={!settings.tab_switch_flag}
              />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Forensic Watermark</p>
          <Toggle k="watermark_enabled" label="Show Watermark Overlay" hint="Email + ID floats over the video" />
          <Toggle k="watermark_flash_enabled" label="Periodic ID Flash" hint="Briefly flashes full identity over video" />
          <div className="mt-1">
            <Label className="text-xs">Center flash interval (sec)</Label>
            <Input
              type="number"
              min={10}
              max={300}
              value={settings.watermark_flash_interval_seconds}
              onChange={(e) => update("watermark_flash_interval_seconds", Math.max(10, parseInt(e.target.value) || 25))}
              className="h-8 text-xs"
              disabled={!settings.watermark_flash_enabled}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Label className="text-xs">Top-left interval (sec)</Label>
              <Input
                type="number"
                min={0}
                max={300}
                value={settings.watermark_corner_tl_interval_seconds}
                onChange={(e) => update("watermark_corner_tl_interval_seconds", Math.max(0, parseInt(e.target.value) || 0))}
                className="h-8 text-xs"
                disabled={!settings.watermark_enabled}
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">0 = off</p>
            </div>
            <div>
              <Label className="text-xs">Bottom-right interval (sec)</Label>
              <Input
                type="number"
                min={0}
                max={300}
                value={settings.watermark_corner_br_interval_seconds}
                onChange={(e) => update("watermark_corner_br_interval_seconds", Math.max(0, parseInt(e.target.value) || 0))}
                className="h-8 text-xs"
                disabled={!settings.watermark_enabled}
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">0 = off</p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Legal Deterrents</p>
          <Toggle k="legal_warning_enabled" label="Pre-play Legal Warning" hint="Student must accept ToS before each video plays" />
        </div>
      </div>

      <Separator />

      {/* Advanced Protections */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md bg-muted/40 hover:bg-muted/60 px-3 py-2 text-left transition-colors"
          >
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Advanced Protections</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-background/60 px-1.5 py-0.5 rounded">Pro</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Device & Environment</p>
              <div className="py-2">
                <Label className="text-sm font-medium">Max Devices per User</Label>
                <p className="text-[11px] text-muted-foreground mb-1">Concurrent active sessions allowed (1–10)</p>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={settings.max_devices_per_user}
                  onChange={(e) => update("max_devices_per_user", Math.max(1, Math.min(10, parseInt(e.target.value) || 2)))}
                  className="h-8 text-xs w-24"
                />
              </div>
              <Toggle k="block_vm_emulator" label="Block VM / Emulator" hint="Detects virtual machines & headless browsers" />
              <Toggle k="block_multi_monitor" label="Block Multi-Monitor" hint="Pauses if extended displays are detected" />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Enforcement & Identity</p>
              <div className="py-2">
                <Label className="text-sm font-medium">Violation Action</Label>
                <p className="text-[11px] text-muted-foreground mb-1">What happens when a violation threshold is hit</p>
                <Select
                  value={settings.violation_action}
                  onValueChange={(v) => update("violation_action", v as ViolationAction)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">⚠️ Warn only</SelectItem>
                    <SelectItem value="pause">⏸ Pause video</SelectItem>
                    <SelectItem value="kick">🚪 Kick from session</SelectItem>
                    <SelectItem value="revoke_enrollment">⛔ Revoke enrollment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="py-2">
                <Label className="text-sm font-medium">Watermark Identity</Label>
                <p className="text-[11px] text-muted-foreground mb-1">Which identifier appears in the forensic overlay</p>
                <Select
                  value={settings.watermark_identity}
                  onValueChange={(v) => update("watermark_identity", v as WatermarkIdentity)}
                  disabled={!settings.watermark_enabled}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">📧 Email only</SelectItem>
                    <SelectItem value="phone">📱 Phone only</SelectItem>
                    <SelectItem value="both">🔐 Email + Phone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-3 h-3 mr-1" /> Reset to defaults
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default SecuritySettingsPanel;
